import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import client from "@/api/client";
import { useSiteContent } from "@/context/SiteContentContext";
import { Loader2, Upload, RotateCcw, Archive, Trash2, Palette, Anchor } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "/logo_transparent.png";

function FieldPair({ label, valueEn, valueAr, onChangeEn, onChangeAr, textarea, testIdBase }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {textarea ? (
          <textarea
            className="rounded-xl border border-input bg-transparent px-3 py-2 text-sm min-h-20 resize-y"
            value={valueEn}
            onChange={(e) => onChangeEn(e.target.value)}
            placeholder="English"
            data-testid={testIdBase ? `${testIdBase}-en` : undefined}
          />
        ) : (
          <Input value={valueEn} onChange={(e) => onChangeEn(e.target.value)} placeholder="English" className="rounded-xl" data-testid={testIdBase ? `${testIdBase}-en` : undefined} />
        )}
        {textarea ? (
          <textarea
            dir="rtl"
            className="rounded-xl border border-input bg-transparent px-3 py-2 text-sm min-h-20 resize-y"
            value={valueAr}
            onChange={(e) => onChangeAr(e.target.value)}
            placeholder="العربية"
            data-testid={testIdBase ? `${testIdBase}-ar` : undefined}
          />
        ) : (
          <Input dir="rtl" value={valueAr} onChange={(e) => onChangeAr(e.target.value)} placeholder="العربية" className="rounded-xl" data-testid={testIdBase ? `${testIdBase}-ar` : undefined} />
        )}
      </div>
    </div>
  );
}

function PanelHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <h2 className="font-display text-lg sm:text-xl font-semibold">{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

/**
 * AdminSiteSettings — the three small CMS/config forms (platform settings,
 * branding, footer). Extracted out of AdminPanel so the shell stays a thin
 * router over section panels.
 */
export function AdminSiteSettings({ panel }) {
  const { t } = useTranslation();
  const { content, refresh } = useSiteContent();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (content) setForm(JSON.parse(JSON.stringify(content)));
  }, [content]);

  const update = useCallback((path, value) => {
    setForm((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  }, []);

  const saveSection = async (sectionName, payload) => {
    setSaving(sectionName);
    try {
      const res = await client.put("/admin/content", payload);
      setForm(res.data);
      await refresh();
      toast.success(t("admin.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(null);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await client.post("/admin/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("logo_url", res.data.url);
      await refresh();
      toast.success(t("admin.logoUpdated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setUploading(false);
    }
  };

  const purgeLogs = async () => {
    setSaving("purge");
    try {
      const res = await client.delete("/admin/access-logs");
      toast.success(t("settingsPanel.purgeDone", {
        signins: res.data.deleted || 0,
        changes: res.data.audit_deleted || 0,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async () => {
    setSaving("reset");
    try {
      const res = await client.post("/admin/content/reset");
      setForm(res.data);
      await refresh();
      toast.success(t("admin.resetDone"));
    } catch (e) {
      toast.error(t("common.error"));
    } finally {
      setSaving(null);
    }
  };

  if (!form) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (panel === "branding") {
    return (
      <Card className="rounded-2xl border border-border p-5 sm:p-6 space-y-6 w-full max-w-5xl" data-testid="admin-panel-branding">
        <PanelHeader icon={Palette} title={t("admin.branding")} subtitle={t("admin.currentLogo")} />
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-border p-4 bg-muted/30">
          <img
            src={form.logo_url || LOGO_URL}
            alt="Logo preview"
            style={{ height: `${Math.round(28 * (form.logo_scale || 1.3))}px` }}
            className="w-auto object-contain self-start"
            data-testid="admin-logo-preview"
          />
          <label className="cursor-pointer sm:ms-auto">
            <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={handleLogoUpload} data-testid="admin-logo-upload-input" />
            <Button asChild variant="outline" className="rounded-xl gap-2" type="button">
              <span>{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("admin.uploadLogo")}</span>
            </Button>
          </label>
        </div>
        <div>
          <Label>{t("admin.logoScale")}: {(form.logo_scale || 1.3).toFixed(1)}x</Label>
          <Slider
            value={[form.logo_scale || 1.3]}
            min={0.5}
            max={2.5}
            step={0.1}
            onValueChange={([v]) => update("logo_scale", v)}
            className="mt-3"
            data-testid="admin-logo-scale-slider"
          />
        </div>
        <Button
          className="btn-brand rounded-xl"
          onClick={() => saveSection("branding", { logo_url: form.logo_url, logo_scale: form.logo_scale })}
          disabled={saving === "branding"}
          data-testid="admin-save-branding"
        >
          {saving === "branding" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
        </Button>
      </Card>
    );
  }

  if (panel === "footer") {
    return (
      <Card className="rounded-2xl border border-border p-5 sm:p-6 space-y-5 w-full max-w-5xl" data-testid="admin-panel-footer">
        <PanelHeader icon={Anchor} title={t("admin.footer")} />
        <FieldPair label={t("admin.tagline")} textarea valueEn={form.footer.tagline_en} valueAr={form.footer.tagline_ar}
          onChangeEn={(v) => update("footer.tagline_en", v)} onChangeAr={(v) => update("footer.tagline_ar", v)} testIdBase="admin-footer-tagline" />
        <FieldPair label={t("admin.rights")} valueEn={form.footer.rights_en} valueAr={form.footer.rights_ar}
          onChangeEn={(v) => update("footer.rights_en", v)} onChangeAr={(v) => update("footer.rights_ar", v)} testIdBase="admin-footer-rights" />
        <Button className="btn-brand rounded-xl" onClick={() => saveSection("footer", { footer: form.footer })} disabled={saving === "footer"} data-testid="admin-save-footer">
          {saving === "footer" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
        </Button>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 xl:grid-cols-2 items-start w-full max-w-6xl">
      <Card className="rounded-2xl border border-border p-5 sm:p-6 space-y-5" data-testid="admin-panel-settings">
        <PanelHeader icon={Archive} title={t("settingsPanel.retentionTitle")} subtitle={t("settingsPanel.retentionDesc")} />
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <Label htmlFor="retention-days">{t("settingsPanel.retentionDays")}</Label>
            <Input
              id="retention-days"
              type="number"
              min={7}
              max={3650}
              value={form.log_retention_days ?? 90}
              onChange={(e) => update("log_retention_days", Math.max(7, Math.min(3650, Number(e.target.value) || 90)))}
              className="mt-1.5 rounded-xl h-10 w-full sm:w-40"
              data-testid="admin-retention-days"
            />
          </div>
          <Button
            className="btn-brand rounded-xl"
            onClick={() => saveSection("settings", { log_retention_days: form.log_retention_days ?? 90 })}
            disabled={saving === "settings"}
            data-testid="admin-save-settings"
          >
            {saving === "settings" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
          </Button>
        </div>
        <div className="rounded-xl border border-border p-4 space-y-2">
          <p className="text-sm font-medium">{t("settingsPanel.purgeNow")}</p>
          <p className="text-[11px] text-muted-foreground">{t("settingsPanel.purgeHint", { days: form.log_retention_days ?? 90 })}</p>
          <Button
            variant="outline"
            className="rounded-xl gap-2"
            onClick={purgeLogs}
            disabled={saving === "purge"}
            data-testid="admin-purge-logs"
          >
            {saving === "purge" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {t("settingsPanel.purgeAction")}
          </Button>
        </div>
      </Card>

      <Card className="rounded-2xl border border-dashed border-border p-5 sm:p-6" data-testid="admin-panel-danger">
        <p className="font-display text-base font-semibold">{t("admin.resetAll")}</p>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          {t("adminConsole.resetHint", { defaultValue: "Reverts every CMS text block back to the shipped defaults. Uploaded media is kept." })}
        </p>
        <Button
          variant="outline"
          className="rounded-xl gap-2 text-rose-600 border-rose-500/30 hover:bg-rose-500/5"
          onClick={handleReset}
          disabled={saving === "reset"}
          data-testid="admin-reset-all"
        >
          {saving === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
          {t("admin.resetAll")}
        </Button>
      </Card>
    </div>
  );
}

export default AdminSiteSettings;
